import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import '../styles/TableViewer.css';

const toFiniteNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const formatCellValue = (value, formatType) => {
    if (value === null || value === undefined || value === '') return '—';
    if (value && typeof value === 'object' && value.seconds && value.nanoseconds) {
        return new Date(value.seconds * 1000).toLocaleString();
    }
    const numericValue = toFiniteNumber(value);
    if (numericValue !== null) {
        switch (formatType) {
            case 'currency': return `$${numericValue.toFixed(2)}`;
            case 'float': return numericValue.toFixed(2);
            case 'int': return Math.trunc(numericValue);
            case 'percentage': return `${(numericValue * 100).toFixed(2)}%`;
            default: return value;
        }
    }
    return value;
};

const sortByPosition = (a, b) => {
    if (a.pos != null && b.pos != null) return a.pos - b.pos;
    if (a.pos != null) return -1;
    if (b.pos != null) return 1;
    return 0;
};

const TableViewer = ({ dataKey, colFormatDict, timestamp, defaultSortBy = 'gp_per_hour', showColumnSelector = true }) => {
    const filteredData = useMemo(() => timestamp ? dataKey.filter((row) => row.week_summary === timestamp) : dataKey, [dataKey, timestamp]);
    const availableColumns = useMemo(() => Object.keys(colFormatDict).map((key) => ({ key, ...colFormatDict[key] })).sort(sortByPosition), [colFormatDict]);
    const initialVisible = useMemo(() => availableColumns.filter((col) => !col.skip).map((col) => col.key), [availableColumns]);
    const [visibleColumns, setVisibleColumns] = useState(initialVisible);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const knownColumns = useRef(availableColumns.map((column) => column.key));

    useEffect(() => {
        const availableKeys = availableColumns.map((column) => column.key);
        const newDefaults = initialVisible.filter((key) => !knownColumns.current.includes(key));
        setVisibleColumns((current) => [...current.filter((key) => availableKeys.includes(key)), ...newDefaults]);
        knownColumns.current = availableKeys;
    }, [availableColumns, initialVisible]);

    useEffect(() => {
        if (!isModalOpen) return undefined;
        const closeOnEscape = (event) => event.key === 'Escape' && setIsModalOpen(false);
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [isModalOpen]);

    const toggleColumn = (colKey) => setVisibleColumns((current) => current.includes(colKey) ? current.filter((id) => id !== colKey) : [...current, colKey]);
    const columns = useMemo(() => availableColumns.filter((col) => visibleColumns.includes(col.key)).map(({ key, ...formatInfo }) => ({
        Header: formatInfo.name || key,
        accessor: key,
        Cell: ({ value }) => formatCellValue(value, formatInfo.type || 'default'),
        justify: formatInfo.just || 'left',
        pos: formatInfo.pos ?? null,
        sortType: ['currency', 'float', 'int', 'percentage'].includes(formatInfo.type) ? 'basic' : 'alphanumeric',
    })), [availableColumns, visibleColumns]);

    const maximums = useMemo(() => {
        const values = {};
        visibleColumns.forEach((columnKey) => {
            const numericValues = filteredData.map((row) => toFiniteNumber(row[columnKey])).filter((value) => value !== null);
            if (numericValues.length) values[columnKey] = Math.max(...numericValues);
        });
        return values;
    }, [filteredData, visibleColumns]);

    const initialSort = columns.some((column) => column.accessor === defaultSortBy) ? [{ id: defaultSortBy, desc: true }] : [];
    const tableInstance = useTable({ columns, data: filteredData || [], initialState: { sortBy: initialSort } }, useSortBy);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    if (!filteredData?.length) return <div className="empty-state">No data available for this period.</div>;

    return (
        <div className="table-wrapper">
            {showColumnSelector && <button type="button" className="column-selector-button" onClick={() => setIsModalOpen(true)}>Customize columns</button>}
            {isModalOpen && (
                <div className="modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setIsModalOpen(false)}>
                    <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="column-modal-title">
                        <div className="popup-header"><div><h3 id="column-modal-title">Customize columns</h3><small>Choose the metrics shown in the table.</small></div><button type="button" className="close-button" onClick={() => setIsModalOpen(false)} aria-label="Close column selector">×</button></div>
                        <div className="modal-columns-grid">
                            {availableColumns.map((col) => {
                                const selected = visibleColumns.includes(col.key);
                                return <button type="button" key={col.key} className={`modal-grid-item ${selected ? 'selected' : ''}`} aria-pressed={selected} onClick={() => toggleColumn(col.key)}>{col.name || col.key}</button>;
                            })}
                        </div>
                        <div className="modal-buttons"><button type="button" onClick={() => setVisibleColumns(initialVisible)}>Restore default</button><button type="button" onClick={() => setIsModalOpen(false)}>Apply columns</button></div>
                    </div>
                </div>
            )}
            <div className="table-container">
                <table {...getTableProps()}>
                    <thead>{headerGroups.map((group) => <tr {...group.getHeaderGroupProps()} key={group.id}>{group.headers.map((column) => <th key={column.id} {...column.getHeaderProps(column.getSortByToggleProps())} style={{ textAlign: column.justify }}>{column.render('Header')}<span aria-hidden="true">{column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}</span></th>)}</tr>)}</thead>
                    <tbody {...getTableBodyProps()}>{rows.map((row) => {
                        prepareRow(row);
                        return <tr {...row.getRowProps()} key={row.id}>{row.cells.map((cell) => {
                            const numericValue = toFiniteNumber(cell.value);
                            const isLeader = numericValue !== null && maximums[cell.column.id] === numericValue;
                            return <td key={`${row.id}-${cell.column.id}`} {...cell.getCellProps({ className: isLeader ? 'leader-cell' : '' })} style={{ textAlign: cell.column.justify }} title={String(cell.value ?? '')}>{cell.render('Cell')}</td>;
                        })}</tr>;
                    })}</tbody>
                </table>
            </div>
        </div>
    );
};

export default TableViewer;
